import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import RollingNumber from "@/components/site/RollingNumber";

const usd = (n) =>
  "$" + Math.round(n).toLocaleString("en-US");
const usd0 = (n) => "$" + Math.round(n).toLocaleString("en-US");

// Reusable labelled range with a tabular readout.
const Assumption = ({ id, label, value, min, max, step, onChange, format }) => (
  <div data-testid={`assumption-${id}`}>
    <div className="flex items-baseline justify-between gap-3 mb-1.5">
      <label htmlFor={id} className="text-xs text-sage leading-snug">
        {label}
      </label>
      <span className="tabular text-sm text-brass font-medium shrink-0" data-testid={`assumption-${id}-value`}>
        {format(value)}
      </span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-spruce cursor-pointer"
      data-testid={`assumption-${id}-input`}
    />
  </div>
);

export const Calculator = ({ config, mode: forcedMode }) => {
  const [mode, setMode] = useState(forcedMode || "rpm");

  const rpmCfg = config?.rpm;
  const bCfg = config?.b340;

  // RPM state
  const [panel, setPanel] = useState(rpmCfg?.input_default ?? 500);
  const [enroll, setEnroll] = useState(rpmCfg?.assumptions?.enrollment_rate?.default ?? 0.5);
  const [comply, setComply] = useState(rpmCfg?.assumptions?.compliance_rate?.default ?? 0.8);
  const [rate, setRate] = useState(rpmCfg?.assumptions?.reimbursement_per_patient?.default ?? 95);

  // 340B state
  const [spend, setSpend] = useState(bCfg?.input_default ?? 2000000);
  const [eligible, setEligible] = useState(bCfg?.assumptions?.eligible_share?.default ?? 0.8);
  const [discount, setDiscount] = useState(bCfg?.assumptions?.discount_rate?.default ?? 0.25);

  const rpm = useMemo(() => {
    const enrolled = panel * enroll;
    const billing = enrolled * comply;
    const monthly = billing * rate;
    return { enrolled, billing, monthly };
  }, [panel, enroll, comply, rate]);

  const b340 = useMemo(() => {
    const eligibleSpend = spend * eligible;
    const reduction = eligibleSpend * discount;
    return { eligibleSpend, reduction };
  }, [spend, eligible, discount]);

  const showToggle = !forcedMode;

  return (
    <div
      className="border border-sage/60 rounded-[2px] bg-bone shadow-none"
      data-testid="calculator"
    >
      {showToggle && (
        <div className="grid grid-cols-2 border-b border-sage/60">
          <button
            onClick={() => setMode("rpm")}
            data-testid="calc-mode-rpm"
            className={`py-3 text-sm font-medium transition-colors ${
              mode === "rpm" ? "bg-spruce text-bone" : "bg-bone text-ink/70 hover:text-spruce"
            }`}
          >
            RPM — patient visits
          </button>
          <button
            onClick={() => setMode("b340")}
            data-testid="calc-mode-340b"
            className={`py-3 text-sm font-medium transition-colors border-l border-sage/60 ${
              mode === "b340" ? "bg-spruce text-bone" : "bg-bone text-ink/70 hover:text-spruce"
            }`}
          >
            340B — specialty drugs
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2">
        {/* Inputs */}
        <div className="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-sage/60">
          <div className="tabular text-xs uppercase tracking-[0.18em] text-sage mb-6">
            Your numbers · editable
          </div>

          {mode === "rpm" ? (
            <div className="space-y-7">
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label htmlFor="panel" className="text-sm text-ink font-medium">
                    {rpmCfg?.input_label}
                  </label>
                  <span className="tabular text-base text-brass font-medium" data-testid="panel-value">
                    {Math.round(panel).toLocaleString("en-US")}
                  </span>
                </div>
                <input
                  id="panel"
                  type="range"
                  min={100}
                  max={5000}
                  step={50}
                  value={panel}
                  onChange={(e) => setPanel(parseFloat(e.target.value))}
                  className="w-full accent-spruce cursor-pointer"
                  data-testid="panel-input"
                />
              </div>
              <Assumption id="enroll" label={rpmCfg?.assumptions?.enrollment_rate?.label} value={enroll} min={0.05} max={1} step={0.05} onChange={setEnroll} format={(v) => `${Math.round(v * 100)}%`} />
              <Assumption id="comply" label={rpmCfg?.assumptions?.compliance_rate?.label} value={comply} min={0.1} max={1} step={0.05} onChange={setComply} format={(v) => `${Math.round(v * 100)}%`} />
              <Assumption id="rate" label={rpmCfg?.assumptions?.reimbursement_per_patient?.label} value={rate} min={20} max={200} step={5} onChange={setRate} format={(v) => usd(v)} />
            </div>
          ) : (
            <div className="space-y-7">
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label htmlFor="spend" className="text-sm text-ink font-medium">
                    {bCfg?.input_label}
                  </label>
                  <span className="tabular text-base text-brass font-medium" data-testid="spend-value">
                    {usd0(spend)}
                  </span>
                </div>
                <input
                  id="spend"
                  type="range"
                  min={250000}
                  max={20000000}
                  step={250000}
                  value={spend}
                  onChange={(e) => setSpend(parseFloat(e.target.value))}
                  className="w-full accent-spruce cursor-pointer"
                  data-testid="spend-input"
                />
              </div>
              <Assumption id="eligible" label={bCfg?.assumptions?.eligible_share?.label} value={eligible} min={0.1} max={1} step={0.05} onChange={setEligible} format={(v) => `${Math.round(v * 100)}%`} />
              <Assumption id="discount" label={bCfg?.assumptions?.discount_rate?.label} value={discount} min={0.05} max={0.5} step={0.01} onChange={setDiscount} format={(v) => `${Math.round(v * 100)}%`} />
              <p className="text-xs text-signal leading-relaxed border-l-2 border-signal pl-3" data-testid="calc-precondition">
                {bCfg?.precondition}
              </p>
            </div>
          )}
        </div>

        {/* Outputs */}
        <div className="p-6 sm:p-8 bg-spruce text-bone">
          <div className="tabular text-xs uppercase tracking-[0.18em] text-sage mb-6">
            Illustrative estimate
          </div>

          {mode === "rpm" ? (
            <div className="space-y-7" data-testid="rpm-outputs">
              <div>
                <div className="text-sm text-sage mb-1">Enrolled patients</div>
                <RollingNumber value={rpm.enrolled} className="text-3xl font-medium text-bone" data-testid="out-enrolled" />
              </div>
              <div>
                <div className="text-sm text-sage mb-1">Patients meeting the 16-day threshold</div>
                <RollingNumber value={rpm.billing} className="text-3xl font-medium text-bone" data-testid="out-billing" />
              </div>
              <div className="pt-6 border-t border-sage/30">
                <div className="text-sm text-sage mb-1">Estimated monthly reimbursement</div>
                <RollingNumber value={rpm.monthly} format={(n) => usd(n)} className="text-4xl font-medium text-brass" data-testid="out-monthly" />
                <div className="tabular text-xs text-sage mt-1">
                  ≈ {usd(rpm.monthly * 12)} / year
                </div>
              </div>
              <p className="text-xs text-sage leading-relaxed" data-testid="rpm-note">{rpmCfg?.note}</p>
            </div>
          ) : (
            <div className="space-y-7" data-testid="b340-outputs">
              <div>
                <div className="text-sm text-sage mb-1">Spend on 340B-eligible drugs</div>
                <RollingNumber value={b340.eligibleSpend} format={(n) => usd(n)} className="text-3xl font-medium text-bone" data-testid="out-eligible" />
              </div>
              <div className="pt-6 border-t border-sage/30">
                <div className="text-sm text-sage mb-1">Illustrative annual drug-cost reduction</div>
                <RollingNumber value={b340.reduction} format={(n) => usd(n)} className="text-4xl font-medium text-brass" data-testid="out-reduction" />
              </div>
              <p className="text-xs text-sage leading-relaxed" data-testid="b340-note">{bCfg?.note}</p>
            </div>
          )}

          <Link
            to="/contact"
            state={{ path: mode === "rpm" ? "rpm" : "340b" }}
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-5 py-3 rounded-[2px] hover:brightness-95 transition-all"
            data-testid="calc-cta"
          >
            {mode === "rpm" ? "See if RPM fits your panel" : "Check 340B for your practice"}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
